import React, { Component } from 'react'
import PropTypes from 'prop-types'
import {
  fetchEvents,
  updateEventRequestUrl,
  fetchEventDescriptions,
  testEventRequestUrl
} from 'api/developerApi'
import { Input, Button } from '@driftt/tide-core'
import isURL from 'validator/lib/isURL'
import dividerSrc from '../divider.svg'
import AppEventsSection from './AppEventsSection'
import { defaultTo } from 'ramda'
import cx from 'classnames'
import 'styles/input.css'
import './style.css'
import { prependHTTPSAlways } from 'utils/validation'

const defaultToHTTPS = (value) => defaultTo('https://')(value)

const validateUrl = (value) => {
  const constraints = { protocols: ['https'], require_protocol: true }
  return isURL(value, constraints)
}

const orEmptyArray = defaultTo([])


class AppEvents extends Component {

  static propTypes = {
    appId: PropTypes.string.isRequired,
    showErrorNotification: PropTypes.string.isRequired
  }

  state = {
    isFetchingData: true,
    requestUrl: '',
    storedRequestUrl: '',
    isSavingRequestUrl: false,
    subscriptions: [],
    eventDescriptions: []
  }

  componentDidMount = () => {
    this.fetchData()
  }

  updateSubscriptions(subscriptions) {
    this.setState({ subscriptions }) 
  }

  fetchData = () => {
    const onDone = ([{ requestUrl, subscriptions }, eventDescriptions]) => {

      this.setState({
        requestUrl: defaultToHTTPS(requestUrl),
        subscriptions: orEmptyArray(subscriptions),
        eventDescriptions: eventDescriptions.events,
        isFetchingData: false
      })
    }
    const onError = (error) => {
      this.setState({ isFetchingData: false })
      this.props.showErrorNotification('Oops, something went wrong. Please refresh the page.')
    }

    Promise.all([
      fetchEvents(this.props.appId),
      fetchEventDescriptions()
    ])
    .then(onDone)
    .catch(onError)
  }

  showTestStatusMessage = (statusCode) => {
    this.setState({ requestUrlTestStatusCode: statusCode, showTestStatusMessage: true })
    setTimeout(() => {
      this.setState({ showTestStatusMessage: false })
    }, 5000)
  }

  canSaveRequestUrl = () => {
    const { requestUrl, storedRequestUrl } = this.state
    const isValidUrl = validateUrl(requestUrl)
    return isValidUrl && (requestUrl !== storedRequestUrl)
  }

  formatRequestUrl = ({ target: { value = '' }}) => this.setState({ requestUrl: prependHTTPSAlways(value) })

  updateRequestUrl = () => {
    const { appId } = this.props
    const { requestUrl } = this.state

    const onSuccess = () => this.setState({ storedRequestUrl: requestUrl })
    const onError = (error) => this.props.showErrorNotification('We couldn’t update your request URL. Please try again.')
    const onFinally = () => this.setState({ isSavingRequestUrl: false })

    const onStateSet = () => updateEventRequestUrl({ appId, requestUrl })
      .then(onSuccess)
      .catch(onError)
      .then(onFinally)

    this.setState({ isSavingRequestUrl: true }, onStateSet)
  }

  testRequestUrl = () => {
    const { requestUrl } = this.state

    const onSuccess = ({ data }) => this.showTestStatusMessage(data.hookStatusCode)
    const onError = error => this.props.showErrorNotification('We couldn’t test your request URL. Please try again.')
    const onFinally = () => this.setState({ isTestingUrlInProgress: false })

    const onStateSet = () =>
      testEventRequestUrl({ requestUrl })
        .then(onSuccess)
        .catch(onError)
        .then(onFinally)

    this.setState({ isTestingUrlInProgress: true }, onStateSet)
  }

  render() {
    const { appId, showErrorNotification } = this.props
    const {
      requestUrl,
      requestUrlTestStatusCode,
      showTestStatusMessage,
      isFetchingData,
      isSavingRequestUrl,
      isTestingUrlInProgress,
      subscriptions,
      eventDescriptions
    } = this.state

    return (
      <div className="app-events">
        <span className="page-title">
          Webhooks
        </span>
        <p className="page-description">
          Your app can be notified of events in Drift (for example, when a conversation is started) at a secure URL you choose.
        </p>

        <div className="request-url">
          <Input
            label="Request URL"
            labelType="strong"
            description="We’ll send HTTPS POST requests to your URL when events occur."
            value={requestUrl}
            onChange={this.formatRequestUrl}
            className={cx('app-events-request-url-input', {
              'drift-input-loading': isFetchingData,
              'drift-input-loaded': !isFetchingData,
            })}
          />

          <Button type="primary" 
            className="app-events-test-url-button" 
            disabled={!this.canSaveRequestUrl()} 
            loading={isTestingUrlInProgress} 
            onClick={this.testRequestUrl}>
            Send Test Payload
          </Button>

          <span
              className={cx('test-url-message', {
                hidden: !showTestStatusMessage,
                failed: requestUrlTestStatusCode === 500
              })}
            >
              {requestUrlTestStatusCode === 500 ? 'Failed' : 'Success'}
          </span>
        </div>

        <Button
          type="primary"
          size="medium"
          className="app-events-save-url-button"
          disabled={!this.canSaveRequestUrl()}
          loading={isSavingRequestUrl}
          onClick={this.updateRequestUrl}>
          Save
        </Button>

        <img
          className="app-events-divider"
          src={dividerSrc}
          alt="dashed line"
        />

        <AppEventsSection
          isSelectorDisabled={isFetchingData}
          subscriptions={subscriptions}
          appId={appId}
          showErrorNotification={showErrorNotification}
          eventDescriptions={eventDescriptions}
          updateSubscriptions={(data) => this.updateSubscriptions(data)}
        />

      </div>
    )
  }
}

export default AppEvents
